// The frame around a product's pages, in that product's own look (CLAUDE.md "Look and copy": inside
// a product its look fills the screen, and the looks never mix): the header with the way back (to
// the Library, or on a phone from a sub-page to the product's home: phoneBack), the product's mark,
// search across everything they own, the bell and their account, as the product boards draw it;
// the product's menu, as links with the page they're on marked; and
// the footer with the help address (decision 38; no product board draws a footer, a departure listed
// for Adam, as Account's was).
//
//   <ProductFrame product={row} chip={frameChip(row, owned)} menu={[{ key, label, href }]} active="resources" title="...">
//
// chip: the member's group on a mastermind ("Group 2", Accelerator.dc.html's header), on every page
// of it, open or paused (frameChip); no other board draws a chip.
// menu: the items (model.js menuFor), false for none (a secured place opens nothing, POYT.dc.html),
// or left out for the type's whole menu (the paused page, which has no product rows to read).
// phoneNav: what stands in for the menu on a phone (the weeks, on a week page: PhoneWeek.dc.html).
// subPage: the page is under the product's own path (a week, Resources, a tool, Sessions, Start
// here), so on a phone its round arrow goes to the product's home (phoneBack).
// Search finds everything the Library's index has, and recordings by their tags (lib/tagSearch.js).
// A hit that opens 1 of this product's recordings shows its 16:9 frame in front of its words once any
// of the product's recordings has Adam's own thumbnail (decision 47, 12 Oct); other products' hits keep
// their words alone, so the looks never mix.
import { useEffect, useRef, useState } from 'react'
import { Link } from '../lib/router.jsx'
import { SITE, setTitle } from '../config/site.js'
import { useMember } from '../app/member.jsx'
import { useLibraryModel } from '../library/useModel.js'
import { usePhone } from '../lib/media.js'
import { searchIn } from '../library/search.js'
import { mergeResults, useTagSearch } from '../lib/tagSearch.js'
import { useOutside } from '../shell/Header.jsx'
import { BellIcon, SearchIcon } from '../shell/Marks.jsx'
import { menuLabel } from './model.js'
import { RecordingThumb, useHitThumbs } from './RecordingThumb.jsx'
import '../styles/product.css'

// The look key for a product's frame
export function frameLook(product) {
  if (product.look === 'poyt') return 'poyt'
  if (product.look === 'funding_engine') return 'fe'
  if (product.look === 'accelerator') return 'acc'
  return 'master'
}

// The chip in the header: the member's group, on a mastermind only (Accelerator.dc.html: "Group 2").
// owned is the Library's entry for the product (library/model.js ownedProducts), whose cohort is
// their group.
export function frameChip(product, owned) {
  return product?.type === 'mastermind' && owned?.cohort?.name ? owned.cohort.name : null
}

// The whole menu of a type, for a page with no product rows to read (the paused page): the
// Accelerator board's for the mastermind, without its Introductions (Adam, 5 Oct) and with Actions
// after Home (member 14; its paused page draws no menu now, member 13), the 1-2-1 board's for a
// client space
const TYPE_MENUS = {
  mastermind: ['home', 'actions', 'sessions', 'recordings', 'resources', 'group'],
  live_cohort: ['home', 'sessions', 'recordings', 'resources'],
  on_demand: ['home', 'recordings', 'resources'],
  client_space: ['home', 'sessions', 'recordings', 'resources', 'actions'],
  masterclass: ['home'],
}
export function typeMenu(product) {
  const base = product.path || '/'
  return (TYPE_MENUS[product.type] || []).map(key => ({ key, label: menuLabel(key), href: key === 'home' ? base : `${base}/${key}` }))
}

// The round arrow on a phone: from a product's home it goes to the Library, as PhoneWeek.dc.html
// draws it, and from a sub-page to the product's home (Adam, 11 Oct 2026, over PhoneWeek.dc.html,
// whose week page's arrow goes to the Library). The video is only ever its own name (decision 44),
// so its arrow has no "home". The laptop's "Library" is FEWeek.dc.html's on every page.
export function phoneBack(product, subPage) {
  if (!subPage) return { to: '/', label: 'Back to your library' }
  const name = product.type === 'masterclass' ? product.name : `${product.name} home`
  return { to: product.path || '/', label: `Back to ${name}` }
}

function BackArrow() {
  return (
    <svg className="icon-arrow" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  )
}

// The product's mark in its header, as each board draws it, and a link to the product's home:
// the masterclass's own name with no badge (Masterclass.dc.html), the 1-2-1's gold dot and
// "with Adam" (ClientSpace.dc.html), POYT's badge (POYT.dc.html), the name alone elsewhere
function ProductMark({ product }) {
  const to = product.path || '/'
  if (product.type === 'masterclass') {
    return <Link to={to} className="pp-mark"><span className="pp-name pp-name--masterclass">{product.name}</span></Link>
  }
  if (product.type === 'client_space') {
    return (
      <Link to={to} className="pp-mark">
        <span className="pp-name">{product.name}</span>
        <span className="pp-mark-dot" aria-hidden="true" />
        <span className="pp-mark-with">with Adam</span>
      </Link>
    )
  }
  if (frameLook(product) === 'poyt') {
    return (
      <Link to={to} className="pp-mark">
        <span className="pp-badge" aria-hidden="true">{product.name.trim()[0]}</span>
        <span className="pp-name">{product.name}</span>
      </Link>
    )
  }
  return <Link to={to} className="pp-mark"><span className="pp-name">{product.name}</span></Link>
}

function ProductHeader({ product, chip, subPage }) {
  const { markRead } = useMember()
  const model = useLibraryModel()
  const phone = usePhone()
  const [panel, setPanel] = useState(null)
  const [q, setQ] = useState('')
  const ref = useRef(null)
  useOutside(ref, panel !== null && !phone, () => setPanel(null))

  const back = phoneBack(product, subPage)
  const term = q.trim()
  const tags = useTagSearch(q, { enabled: panel === 'search' })
  const results = model && term ? mergeResults(searchIn(model.search, q, 5), tags, 5) : []
  const hits = useHitThumbs(results, model?.searchThumbs, { productId: product.id })

  function toggle(which) {
    const next = panel === which ? null : which
    setPanel(next)
    if (next === 'bell') markRead()
  }

  const searchPanel = (
    <div className="pp-pop pp-pop--search">
      <input aria-label="Search everything you own" placeholder="Search everything you own" value={q}
        onChange={e => setQ(e.target.value)} autoFocus />
      <span className="pp-pop-hint">{term ? 'In everything you own' : 'Recordings, chapters, resources and prompts in every product you own'}</span>
      {hits.rows.map(({ hit: r, card }) => (
        <Link key={r.label + r.where} to={r.href} className={card ? 'pp-pop-row pp-pop-row--thumb' : 'pp-pop-row'} onClick={() => setPanel(null)}>
          <RecordingThumb card={card} thumbs={hits.thumbs} className="hit-thumb" />
          <span className="pp-pop-label">{r.label}</span>
          <span className="pp-pop-sub">{r.where}</span>
        </Link>
      ))}
      {term && results.length === 0 && <span className="pp-pop-none">Nothing matches that in anything you own.</span>}
    </div>
  )
  const bellPanel = (
    <div className="pp-pop pp-pop--bell">
      <span className="pp-pop-head">Across everything you own</span>
      {(model?.notifications || []).map(n => (
        <Link key={n.id} to={n.href} className="pp-pop-row" onClick={() => setPanel(null)}>
          <span className="pp-pop-label pp-pop-label--note">{n.text}</span>
          <span className="pp-pop-sub">{n.when}</span>
        </Link>
      ))}
      {model && model.notifications.length === 0 && <span className="pp-pop-none">Nothing new.</span>}
    </div>
  )

  return (
    <>
      <header className="pp-header">
        <div className="pp-header-left">
          {phone
            ? <Link to={back.to} className="pp-back pp-back--round" aria-label={back.label}><BackArrow /></Link>
            : <Link to="/" className="pp-back"><BackArrow />Library</Link>}
          <span className="pp-divider" aria-hidden="true" />
          <ProductMark product={product} />
        </div>
        <div className="pp-header-right" ref={ref}>
          {chip && <span className="pp-chip">{chip}</span>}
          <button type="button" className="pp-round" aria-label="Search everything you own" aria-expanded={panel === 'search'} onClick={() => toggle('search')}>
            <SearchIcon />
          </button>
          <button type="button" className="pp-round" aria-label="Notifications" aria-expanded={panel === 'bell'} onClick={() => toggle('bell')}>
            <BellIcon size={18} />
            {model?.hasUnread && <span className="pp-unread" aria-hidden="true" />}
          </button>
          <Link to="/account" className="pp-avatar" aria-label="Your account">{model?.initials || ''}</Link>
          {!phone && panel === 'search' && searchPanel}
          {!phone && panel === 'bell' && bellPanel}
        </div>
      </header>
      {phone && panel === 'search' && <div className="pp-panel">{searchPanel}</div>}
      {phone && panel === 'bell' && <div className="pp-panel">{bellPanel}</div>}
    </>
  )
}

// The menu: links, the page they're on marked (aria-current). Where the items don't all fit on 1 line
// (the Accelerator's 6 at 390) they wrap onto the next (product.css), so every item is always in sight.
export function ProductNav({ product, items, active }) {
  if (!items?.length) return null
  return (
    <nav className="pp-nav" aria-label={product.name}>
      {items.map(item => (
        <Link key={item.key} to={item.href} className={item.key === active ? 'pp-nav-item pp-nav-item--on' : 'pp-nav-item'}
          aria-current={item.key === active ? 'page' : undefined}>
          {item.label}
        </Link>
      ))}
    </nav>
  )
}

export function ProductFooter() {
  return (
    <footer className="pp-footer">
      <span>{SITE.company}</span>
      <span className="pp-footer-links">
        <a href={SITE.termsUrl}>Terms</a>
        <a href={SITE.privacyUrl}>Privacy</a>
        <span>Help: {SITE.helpEmail}</span>
      </span>
    </footer>
  )
}

export function ProductFrame({ product, chip = null, menu, active = 'home', phoneNav = null, subPage = false, title, children }) {
  const { ensureLibrary } = useMember()
  const phone = usePhone()
  useEffect(() => { setTitle(title || product.name) }, [title, product.name])
  useEffect(() => { ensureLibrary() }, [ensureLibrary])
  const items = menu === false ? null : Array.isArray(menu) ? menu : typeMenu(product)
  return (
    <div className={`pp pp--${frameLook(product)}${product.type === 'masterclass' ? ' pp--masterclass' : ''}`}>
      <ProductHeader product={product} chip={chip} subPage={subPage} />
      {phone && phoneNav ? phoneNav : <ProductNav product={product} items={items} active={active} />}
      {children}
      <ProductFooter />
    </div>
  )
}
